import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { ApiResponse } from "../../domain/models/http/Response";
import type { Search, SearchFilter, SearchInstruction, SearchSort } from "../../domain/models/http/SearchRequest";
import { RequestModel } from "../../domain/models/Request";
import { SiteModel } from "../../domain/models/Site";
import { SubsidiaryModel } from "../../domain/models/Subsidiary";
import { userQueryServiceForRequest } from "../../services/request/RequestQueryService";
import { siteQueryService } from "../../services/sites/SiteQueryService";
import { subsidiaryQueryService } from "../../services/subsidiaries/SubsidiaryQueryService";
import { requestStepsQueryService } from "../../services/request/RequestStepsQueryService";
import { StepModel } from "../../domain/models/Steps";
import { RequestCheckModel } from "../../domain/models/Request";
import { requestCheckQueryService } from "../../services/request/RequestCheckQueryService";
import { StatusModel } from "../../domain/models/Status";
import { statusQueryService } from "../../domain/core/status/StatusQuery";
export const useSites = (search, options) => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== search) {
    t0 = ["sites", search];
    t1 = () => siteQueryService.search(search ? {
      search
    } : undefined);
    $[0] = search;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== options || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[3] = options;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
};
export const useSubsidiaries = (search, options) => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== search) {
    t0 = ["subsidiaries", search];
    t1 = () => subsidiaryQueryService.search(search ? {
      search
    } : undefined);
    $[0] = search;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== options || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[3] = options;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
};
export const useRequestSteps = options => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["request_steps"],
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
};
export const useRequestStatuses = options => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["request_status"],
      queryFn: _temp2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
};
export const useRequests = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["requests"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      queryKey: t0,
      queryFn: _temp4,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
};
export const useRequestChecks = options => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["request_checks"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== options) {
    t1 = {
      queryKey: t0,
      queryFn: _temp5,
      ...options
    };
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useQuery(t1);
};
type UseRequestsSearchParams = {
  queryKey: QueryKey;
  filters?: SearchFilter[];
  instructions?: SearchInstruction[];
  sorts?: SearchSort[];
  page?: number;
  limit?: number;
};
export const useRequestsSearch = t0 => {
  const $ = _c(15);
  const {
    queryKey,
    filters: t1,
    instructions: t2,
    sorts: t3,
    page: t4,
    limit: t5
  } = t0;
  let t6;
  if ($[0] !== t1) {
    t6 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const filters = t6;
  let t7;
  if ($[2] !== t2) {
    t7 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const instructions = t7;
  let t8;
  if ($[4] !== t3) {
    t8 = t3 === undefined ? [{
      field: "requested_at",
      direction: "desc"
    }] : t3;
    $[4] = t3;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const sorts = t8;
  const page = t4 === undefined ? 1 : t4;
  const limit = t5 === undefined ? 10 : t5;
  let t9;
  if ($[6] !== filters || $[7] !== instructions || $[8] !== limit || $[9] !== page || $[10] !== sorts) {
    t9 = () => userQueryServiceForRequest.search({
      search: {
        includes: [{
          relation: "user"
        }, {
          relation: "site"
        }, {
          relation: "subsidiary"
        }, {
          relation: "requestStatus"
        }, {
          relation: "requestStep"
        }, {
          relation: "requestStepAssignment"
        }, {
          relation: "requestType"
        }, {
          relation: "requestable"
        }, {
          relation: "comments"
        }],
        ...(filters.length > 0 && {
          filters
        }),
        ...(instructions.length > 0 && {
          instructions
        }),
        ...(sorts.length > 0 && {
          sorts
        }),
        page,
        limit
      }
    });
    $[6] = filters;
    $[7] = instructions;
    $[8] = limit;
    $[9] = page;
    $[10] = sorts;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== queryKey || $[13] !== t9) {
    t10 = {
      queryKey,
      queryFn: t9
    };
    $[12] = queryKey;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return useQuery(t10);
};
function _temp() {
  return requestStepsQueryService.search();
}
function _temp2() {
  return statusQueryService.search();
}
function _temp3(page) {
  return page.data;
}
async function _temp4() {
  const search = {
    includes: [{
      relation: "user"
    }, {
      relation: "site"
    }, {
      relation: "subsidiary"
    }, {
      relation: "requestStatus"
    }, {
      relation: "requestStep"
    }, {
      relation: "requestType"
    }, {
      relation: "requestable"
    }],
    sorts: [{
      field: "requested_at",
      direction: "desc"
    }],
    limit: 50
  };
  const firstPage = await userQueryServiceForRequest.search({
    search
  });
  if (firstPage.last_page <= 1) {
    return firstPage;
  }
  const remainingPages = await Promise.all(Array.from({
    length: firstPage.last_page - 1
  }, (_, index) => userQueryServiceForRequest.search({
    search: {
      ...search,
      page: index + 2
    }
  })));
  return {
    ...firstPage,
    data: [...firstPage.data, ...remainingPages.flatMap(_temp3)],
    from: firstPage.data.length > 0 ? 1 : 0,
    to: firstPage.total,
    per_page: firstPage.total || firstPage.per_page
  };
}
function _temp5() {
  return requestCheckQueryService.search();
}